import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { CacheProvider } from "@emotion/react";
import type { EmotionCache } from "@emotion/cache";
import { BfrsProvider, type BfrsColorScheme } from "@/components/ui/providers/BfrsProvider";
import { getShadowEmotionCache } from "@/lib/emotionCache";
import { adoptBfrsStyles, ensureDocumentStyles } from "./shadowStyles";

export type BfrsShadowRootProps = {
  children: ReactNode;
  /** Color scheme applied inside the shadow root. Defaults to `"light"`. */
  colorScheme?: BfrsColorScheme;
  /** Class applied to the light-DOM host element (not the shadow content). */
  className?: string;
  /** Style applied to the light-DOM host element. */
  style?: CSSProperties;
};

/**
 * Renders its React children inside an open shadow root so external class names
 * and stylesheets cannot affect them. The compiled, `.bfrs`-scoped library
 * stylesheet is adopted into the shadow root (the `--bfrs-*` token custom
 * properties still pierce the boundary, so theming and overrides keep working).
 *
 * Overlays (Select, Dropdown, Tooltip, Toast, …) portal to `<body>` outside the
 * shadow; their styles are injected into the document automatically.
 *
 * ```tsx
 * <BfrsShadowRoot>
 *   <Button>Encapsulated</Button>
 * </BfrsShadowRoot>
 * ```
 */
export function BfrsShadowRoot({ children, colorScheme = "light", className, style }: BfrsShadowRootProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [scope, setScope] = useState<{ mount: HTMLElement; cache: EmotionCache } | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const shadow = host.shadowRoot ?? host.attachShadow({ mode: "open" });
    adoptBfrsStyles(shadow);
    ensureDocumentStyles();

    let node = shadow.querySelector<HTMLElement>("[data-bfrs-shadow-mount]");
    if (!node) {
      node = document.createElement("div");
      node.setAttribute("data-bfrs-shadow-mount", "");
      node.style.display = "contents";
      shadow.appendChild(node);
    }
    // Emotion styles must be injected into this shadow root — document-level
    // styles cannot cross the boundary.
    setScope({ mount: node, cache: getShadowEmotionCache(shadow) });
  }, []);

  return (
    <div ref={hostRef} className={className} style={style}>
      {scope
        ? createPortal(
            <CacheProvider value={scope.cache}>
              <BfrsProvider colorScheme={colorScheme} style={{ display: "contents" }}>
                {children}
              </BfrsProvider>
            </CacheProvider>,
            scope.mount
          )
        : null}
    </div>
  );
}

BfrsShadowRoot.displayName = "BfrsShadowRoot";
